/**
 * Project activity feed — a cross-task roll-up of everything the field captured
 * (notes, photos/videos, punch items) so PM / Sales / Supervisor can see it in
 * one place on the project screen.
 *
 *   GET /projects/:id/activity
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PunchItemStatusSchema } from './enums.js';
/** A photo/video attached to a note or punch (or standing alone). */
export const ActivityMediaSchema = z.object({
    id: UuidSchema,
    kind: z.enum(['photo', 'video']),
    mime_type: z.string(),
    /** Signed, expiring download URL — what <img>/<video> should use. */
    file_url: z.string().optional(),
});
export const ProjectActivityItemSchema = z.object({
    /** Discriminator for rendering. ('status' = a lifecycle change; cross-project
     *  feed only — title holds the label, body any reason/note.) */
    kind: z.enum(['note', 'punch', 'media', 'status']),
    id: UuidSchema,
    at: IsoDateTimeSchema,
    author_name: z.string().nullable(),
    /** The task this came from (null for project-level items). */
    task_id: UuidSchema.nullable(),
    task_title: z.string().nullable(),
    /** note body (kind=note). */
    body: z.string().nullable(),
    /** punch fields (kind=punch). */
    title: z.string().nullable(),
    blocks_recap: z.boolean().nullable(),
    punch_status: PunchItemStatusSchema.nullable(),
    /** Attached media — grouped under a note/punch, or the lone item for kind=media. */
    media: z.array(ActivityMediaSchema),
});
export const ProjectActivityResponseSchema = z.object({
    items: z.array(ProjectActivityItemSchema),
});
// ----------------------------------------------------------------------------
// Cross-project activity feed (supervisor/admin) — the same items, but across
// every project, each carrying its project so the entry can deep-link.
//   GET /activity?limit=&offset=
// ----------------------------------------------------------------------------
export const ActivityFeedItemSchema = ProjectActivityItemSchema.extend({
    project_id: UuidSchema,
    project_title: z.string(),
});
export const ActivityFeedResponseSchema = z.object({
    items: z.array(ActivityFeedItemSchema),
    has_more: z.boolean(),
});
// ----------------------------------------------------------------------------
// Project-level note (Workspace.3b) — office posts a note on the project itself
// (task_id null). Media attach via the project attachment endpoint + note_id.
// ----------------------------------------------------------------------------
export const ProjectNoteCreateSchema = z.object({
    body: z.string().min(1).max(2000),
    source: z.enum(['text', 'voice']).default('text'),
});
export const ProjectNoteSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    task_id: UuidSchema.nullable(),
    body: z.string(),
    source: z.enum(['text', 'voice']),
    created_at: IsoDateTimeSchema,
});
//# sourceMappingURL=activity.js.map